<template>
  <v-container
    :fluid="embedded"
    :class="['marketing-agent', embedded ? 'marketing-agent--embedded pa-0' : 'layout-container']"
  >
    <BaseHeader
      v-if="!embedded"
      :title="t('marketing-agent.title')"
    >
      <template #desc>
        <p>{{ t('marketing-agent.subtitle') }}</p>
      </template>
    </BaseHeader>

    <div class="marketing-agent__plan">
      <v-card
        class="plan-shell"
        rounded="lg"
        flat
        border
      >
        <v-progress-linear
          v-if="initialLoading"
          class="plan-shell__loader"
          color="primary"
          indeterminate
        />
        <RetryNotice
          v-if="loadError"
          class="plan-shell__notice"
          :message="t('marketing-agent.load-error')"
          :state="planLoad.state.value"
          :loading="reloadingPlan"
          @retry="onReloadPlan"
        />
        <RetryNotice
          v-if="scheduledError"
          class="plan-shell__notice"
          :message="t('marketing-agent.scheduled-load-error')"
          :state="scheduledLoad.state.value"
          :loading="reloadingScheduled"
          @retry="onReloadScheduled"
        />
        <PlanCalendar
          class="plan-shell__calendar"
          :slots="calendarSlots"
          :has-plan="hasPlan"
          :plan-unknown="planUnknown"
          :can-plan-next="canPlanNext"
          :creating="generating"
          :creatable="isOutline"
          @slot-click="onSlotClick"
          @create-slot="onCreateAt"
          @create-plan="wizardOpen = true"
          @delete-plan="deleteDialogOpen = true"
        />

        <div
          v-if="
            hasPlan &&
            (schedulePending ||
              scheduleError ||
              isOutline ||
              isGenerating ||
              imagesPending ||
              failedSlots.length > 0 ||
              approvableSlots.length > 0)
          "
          class="plan-approval"
        >
          <h2 class="plan-approval__title text-white">
            {{
              schedulePending || scheduleError || isOutline
                ? t('marketing-agent.outline.title')
                : t('marketing-agent.approval.title')
            }}
          </h2>
          <div
            v-if="schedulePending"
            class="plan-approval__body"
          >
            <p class="plan-approval__text plan-approval__text--status">
              <v-progress-circular
                indeterminate
                size="16"
                width="2"
                color="primary"
              />
              {{ t('marketing-agent.generating-schedule') }}
            </p>
          </div>
          <div
            v-else-if="scheduleError"
            class="plan-approval__body"
          >
            <p class="plan-approval__text">{{ t('marketing-agent.schedule-failed') }}</p>
            <div class="plan-approval__actions">
              <v-btn
                class="plan-approval__delete"
                color="primary"
                variant="text"
                :loading="!!deletingPlanId"
                @click="deleteDialogOpen = true"
              >
                {{ t('marketing-agent.delete-plan') }}
              </v-btn>
            </div>
          </div>
          <div
            v-else-if="isOutline"
            class="plan-approval__body"
          >
            <p class="plan-approval__text">{{ t('marketing-agent.outline.text') }}</p>
            <div class="plan-approval__actions">
              <v-btn
                class="plan-approval__delete"
                color="primary"
                variant="text"
                :loading="!!deletingPlanId"
                @click="deleteDialogOpen = true"
              >
                {{ t('marketing-agent.delete-plan') }}
              </v-btn>
              <v-btn
                color="primary"
                variant="text"
                prepend-icon="mdi-plus"
                @click="onAddOutlineSlot"
              >
                {{ t('marketing-agent.outline.add') }}
              </v-btn>
              <v-btn
                color="primary"
                variant="flat"
                rounded="pill"
                :loading="acceptingOutline"
                :disabled="!slots.length"
                @click="onAcceptOutline"
              >
                {{ t('marketing-agent.outline.accept') }}
              </v-btn>
            </div>
          </div>
          <div
            v-else
            class="plan-approval__body"
          >
            <p
              v-if="!canReviewPlan"
              class="plan-approval__text plan-approval__text--status"
            >
              <v-progress-circular
                indeterminate
                size="16"
                width="2"
                color="primary"
              />
              {{ t('marketing-agent.generating') }}
            </p>
            <template v-else>
              <p
                v-if="failedSlots.length"
                class="plan-approval__text plan-approval__text--error"
              >
                {{ t('marketing-agent.failed-posts.text', { count: failedSlots.length }) }}
              </p>
              <p
                v-if="unscheduledSkips.length"
                class="plan-approval__text plan-approval__text--error"
              >
                {{ unscheduledText }}
              </p>
              <template v-if="approvableSlots.length">
                <p class="plan-approval__text">{{ t('marketing-agent.approval.text') }}</p>
                <v-checkbox
                  :model-value="responsibilityAccepted"
                  class="plan-approval__check"
                  color="primary"
                  density="comfortable"
                  hide-details
                  :label="t('marketing-agent.approval.responsibility')"
                  @update:model-value="responsibilityAccepted = $event === true"
                />
              </template>
            </template>
            <div class="plan-approval__actions">
              <v-btn
                class="plan-approval__delete"
                color="primary"
                variant="text"
                :loading="!!deletingPlanId"
                :disabled="busyBulk"
                @click="deleteDialogOpen = true"
              >
                {{ t('marketing-agent.delete-plan') }}
              </v-btn>
              <v-btn
                v-if="failedSlots.length"
                color="primary"
                variant="text"
                prepend-icon="mdi-refresh"
                :loading="retryingFailed"
                :disabled="busyBulk"
                @click="onRetryFailed"
              >
                {{ t('marketing-agent.failed-posts.retry') }}
              </v-btn>
              <v-btn
                v-if="canPlanNext"
                class="plan-approval__new"
                color="primary"
                variant="text"
                :loading="generating"
                :disabled="busyBulk"
                @click="wizardOpen = true"
              >
                {{ t('marketing-agent.approval.create-new') }}
              </v-btn>
              <v-btn
                class="plan-approval__confirm"
                color="primary"
                variant="flat"
                rounded="pill"
                :loading="approvingAll"
                :disabled="!canConfirmPlan"
                @click="onApproveAll"
              >
                {{ t('marketing-agent.approval.confirm') }}
              </v-btn>
            </div>
          </div>
        </div>
      </v-card>
    </div>

    <v-dialog
      v-model="wizardOpen"
      max-width="560"
    >
      <PlanWizard
        closable
        :generating="generating"
        :blocked-until="wizardStartsAfter"
        @close="wizardOpen = false"
        @create="onCreatePlan"
      />
    </v-dialog>

    <PlanDeleteDialog
      v-model="deleteDialogOpen"
      :deleting-id="deletingPlanId"
      @delete="onDeletePlan"
      @create="onManageCreate"
    />

    <PlanOutline
      v-model="outlineEditorOpen"
      :slot-data="outlineSlot"
      :known-platforms="outlinePlatforms"
      :saving="savingOutline"
      @save="onSaveOutlineSlot"
      @remove="onRemoveOutlineSlot"
    />

    <SlotDialog
      v-model="dialogOpen"
      :slot-data="selectedSlot"
      :read-only="selectedSlot?.source === 'scheduled'"
      :recreate="onRecreateSlot"
      :update-text="onUpdateSlotText"
      :remove="deleteSlot"
      :approve="approveSlot"
      :update-schedule="onUpdateSlotSchedule"
      :known-platforms="outlinePlatforms"
    />
  </v-container>
</template>

<script setup>
import { useQuery } from '@vue/apollo-composable'
import { computed, inject, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import BaseHeader from '@/components/BaseHeader.vue'
import {
  GenerateMarketingPlanStatus,
  MarketingPlanSlotImageStatus,
  MarketingPlanSlotStatus,
  MarketingPlanSlotTextStatus,
  SlotOutcome,
} from '@/generated/graphql'
import { showSnackbarMessage } from '@/lib/snackbarMessages'
import { useScheduledContents } from '@/modules/contentCreator/creator/composables/useScheduledContents'
import { mergePlanSlotsWithScheduled } from '@/modules/contentCreator/creator/scheduledContents'

import { useMarketingPlan } from './composables/useMarketingPlan'
import { useRetryLimit } from './composables/useRetryLimit'
import { reportError, reportOutcome } from './lib/reportError'
import PlanCalendar from './PlanCalendar.vue'
import PlanDeleteDialog from './PlanDeleteDialog.vue'
import PlanOutline from './PlanOutline.vue'
import PlanWizard from './PlanWizard.vue'
import GET_MARKETING_PLANS from './queries/getMarketingPlans.gql'
import RetryNotice from './RetryNotice.vue'
import SlotDialog from './SlotDialog.vue'

const SLOT_ERROR_MESSAGES = {
  NOT_FOUND: 'marketing-agent.errors.not-found',
  ALREADY_APPROVED: 'marketing-agent.errors.already-approved',
  TEXT_NOT_READY: 'marketing-agent.errors.text-not-ready',
  TEXT_GENERATING: 'marketing-agent.errors.text-generating',
  TEXT_EMPTY: 'marketing-agent.errors.text-empty',
  INVALID_SCHEDULE: 'marketing-agent.errors.invalid-schedule',
  SCHEDULE_IN_PAST: 'marketing-agent.errors.schedule-in-past',
  PLATFORM_NOT_CONNECTED: 'marketing-agent.errors.platform-not-connected',
  OUTLINE_EMPTY: 'marketing-agent.errors.outline-empty',
  INVALID_WINDOW: 'marketing-agent.errors.invalid-window',
  NO_TOPIC: 'marketing-agent.errors.no-topic',
  POST_ALREADY_PUBLISHED: 'marketing-agent.errors.post-already-published',
  POST_TIME_PASSED: 'marketing-agent.errors.post-time-passed',
  APPROVAL_FORBIDDEN: 'marketing-agent.errors.approval-forbidden',
  APPROVAL_REJECTED: 'marketing-agent.errors.approval-rejected',
  APPROVAL_FAILED: 'marketing-agent.errors.approval-failed',
  APPROVAL_NOT_SAVED: 'marketing-agent.errors.approval-not-saved',
  IMAGE_MODEL_UNAVAILABLE: 'marketing-agent.errors.image-model-unavailable',
  NO_COMPANY: 'marketing-agent.errors.no-company',
  DELETE_FAILED: 'marketing-agent.errors.delete-failed',
}

// Causes the user has to fix before approving again, most actionable first.
// APPROVAL_NOT_SAVED leads: the post may be scheduled already, and approving
// again would post it twice.
const SKIP_REASON_PRIORITY = [
  'APPROVAL_NOT_SAVED',
  'SCHEDULE_IN_PAST',
  'APPROVAL_FORBIDDEN',
  'APPROVAL_REJECTED',
]

// A thrown SCHEDULE_IN_PAST rejects a new date; a skipped one means the time has passed.
const THROWN_ERROR_MESSAGES = {
  ...SLOT_ERROR_MESSAGES,
  SCHEDULE_IN_PAST: 'marketing-agent.errors.schedule-too-early',
}

function getSlotErrorCode(error) {
  const gqlErrors = error?.graphQLErrors ?? error?.errors ?? []
  return gqlErrors.find((e) => e?.extensions?.slotErrorCode)?.extensions.slotErrorCode
}

function getThrownErrorMessage(error, fallbackKey) {
  const key = THROWN_ERROR_MESSAGES[getSlotErrorCode(error)]
  return key ? t(key) : t(fallbackKey)
}

// Validation errors are expected, so show their message but don't report them to Sentry.
function reportFailure(operation, error, fallbackKey, context) {
  if (!getSlotErrorCode(error)) reportError(operation, error, context)
  showSnackbarMessage('error', getThrownErrorMessage(error, fallbackKey))
}

const SCHEDULED_POSTS_LINK = { path: '/content-creator', query: { section: 'scheduled-posts' } }

const { t } = useI18n()
const $tracking = inject('tracking')

defineProps({
  embedded: {
    type: Boolean,
    default: false,
  },
})
const {
  planId,
  slots,
  hasPlan,
  approvableSlots,
  generating,
  isGenerating,
  imagesPending,
  schedulePending,
  scheduleError,
  isOutline,
  acceptingOutline,
  initialLoading,
  loadError,
  reload,
  generate,
  refetch,
  updateSlotSchedule,
  addSlot,
  acceptOutline,
  recreateSlotText,
  updateSlotText,
  deleteSlots,
  approveSlots,
  deletePlan,
} = useMarketingPlan()

const {
  scheduledContents,
  error: scheduledError,
  refetch: refetchScheduled,
} = useScheduledContents()
// Failed posts stay on the calendar: opening one is how the user retries,
// writes or deletes that post alone while the rest of the plan is confirmed.
const calendarSlots = computed(() =>
  mergePlanSlotsWithScheduled(slots.value, scheduledContents.value)
)

const dialogOpen = ref(false)
const deleteDialogOpen = ref(false)
const wizardOpen = ref(false)
const outlineEditorOpen = ref(false)
const outlinePrefill = ref(null)
const selectedSlotId = ref(null)
const selectedScheduled = ref(null)
const responsibilityAccepted = ref(false)
const approvingAll = ref(false)
const deletingPlanId = ref(null)
const savingOutline = ref(false)
const reloadingPlan = ref(false)
const reloadingScheduled = ref(false)
const retryingFailed = ref(false)
// Results of the last "Confirm plan" for posts it could not schedule. Shown in the
// review panel, because the snackbar closes before the user can act on it.
const approvalSkips = ref([])
// Id of the plan just generated, until its schedule is written or fails.
const awaitingSchedulePlanId = ref(null)
// Guards the approval actions so they can't overlap.
const busyBulk = computed(
  () => approvingAll.value || !!deletingPlanId.value || retryingFailed.value
)
const IMAGE_REQUEST_DONE = [
  MarketingPlanSlotImageStatus.Ready,
  MarketingPlanSlotImageStatus.Failed,
  MarketingPlanSlotImageStatus.TimedOut,
]

// Without a plan and with a failed load we don't know whether a plan exists.
// Offering "Create plan" then could create a duplicate.
const planUnknown = computed(() => !hasPlan.value && (initialLoading.value || !!loadError.value))

// Posts whose text could not be generated. They can be generated again.
const failedSlots = computed(() =>
  slots.value.filter(
    (slot) =>
      slot.textStatus === MarketingPlanSlotTextStatus.Failed &&
      slot.status !== MarketingPlanSlotStatus.Approved
  )
)

// Failures arrive through polling. Announce each failed post once, and again if
// it fails after a retry, so red posts never appear without an explanation.
const announcedFailures = new Set()
watch(failedSlots, (failed) => {
  const failedIds = new Set(failed.map((slot) => slot.id))
  for (const id of announcedFailures) {
    if (!failedIds.has(id)) announcedFailures.delete(id)
  }
  const fresh = failed.filter((slot) => !announcedFailures.has(slot.id))
  if (!fresh.length) return
  fresh.forEach((slot) => announcedFailures.add(slot.id))
  showSnackbarMessage('error', t('marketing-agent.failed-posts.snackbar', { count: fresh.length }))
})

async function onRecreateSlot(slotId) {
  try {
    return await recreateSlotText(slotId)
  } catch (error) {
    reportFailure('recreate-slot', error, 'marketing-agent.failed-posts.retry-error', {
      planId: planId.value,
      slotId,
    })
    throw error
  }
}

// One request per post. If the cause is still there, the backend stops the
// rest of the plan after the first failure, so this does not flood Genaisis.
async function onRetryFailed() {
  retryingFailed.value = true
  const slots = failedSlots.value
  try {
    const results = await Promise.allSettled(slots.map((slot) => recreateSlotText(slot.id)))
    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        $tracking.heapEvent('marketing_plan_slot_regenerated', { platform: slots[index].platform })
      }
    })
    const rejected = results
      .map((result, index) => ({ result, slotId: slots[index].id }))
      .filter(({ result }) => result.status === 'rejected')
    for (const { result, slotId } of rejected) {
      reportError('recreate-slot', result.reason, { planId: planId.value, slotId })
    }
    if (rejected.length) {
      showSnackbarMessage('error', t('marketing-agent.failed-posts.retry-error'))
    }
  } finally {
    // A request can fail on the network after the server accepted it. Reload so
    // the retried posts don't stay red and get sent twice.
    await refreshPlan()
    retryingFailed.value = false
  }
}

const planLoad = useRetryLimit('load-plan', { error: loadError })
const scheduledLoad = useRetryLimit('load-scheduled', { error: scheduledError })

// The responsibility confirmation belongs to the plan the user reviewed.
watch(planId, () => {
  responsibilityAccepted.value = false
  approvalSkips.value = []
})

// Only posts still waiting for approval, so the note goes once each is scheduled,
// edited away or deleted.
const unscheduledSkips = computed(() => {
  const waiting = new Set(approvableSlots.value.map((slot) => slot.id))
  return approvalSkips.value.filter((result) => waiting.has(result.slotId))
})

const unscheduledText = computed(() => {
  const skips = unscheduledSkips.value
  const reason =
    skippedReason(skips) || getSlotErrorMessage(skips[0], 'marketing-agent.approve.none')
  return `${t('marketing-agent.approve.not-scheduled', { count: skips.length })} ${reason}`
})

// The responsibility checkbox appears only after every image request has
// finished: success, failure, or timeout. A missing status is still pending.
const canReviewPlan = computed(() => {
  const generatingSlots = slots.value.filter(
    (slot) => slot.textStatus !== MarketingPlanSlotTextStatus.Draft
  )
  return (
    generatingSlots.length > 0 &&
    generatingSlots.every((slot) => IMAGE_REQUEST_DONE.includes(slot.imageStatus))
  )
})

// Next iteration waits until this plan has left the draft and generation has finished.
const canPlanNext = computed(() => hasPlan.value && !isOutline.value && canReviewPlan.value)

const nextIterationStart = computed(() => {
  const lastDate = slots.value
    .map((slot) => slot?.scheduledLocal?.date)
    .filter(Boolean)
    .sort()
    .at(-1)
  if (!lastDate) return null
  const [year, month, day] = lastDate.split('-').map(Number)
  const next = new Date(year, month - 1, day)
  next.setDate(next.getDate() + 1)
  return next
})

// Plans follow one another, so the wizard starts after every open plan, not only
// the one on screen. Loaded while the wizard is open; the backend checks too.
const { result: plansResult, refetch: refetchPlans } = useQuery(GET_MARKETING_PLANS, null, () => ({
  enabled: wizardOpen.value,
  fetchPolicy: 'network-only',
  errorPolicy: 'all',
}))

const wizardStartsAfter = computed(() => {
  // startDate is the plan's calendar day at UTC midnight.
  const lastDay = (plansResult.value?.plans ?? [])
    .flatMap((plan) => {
      const dates = (plan.slots ?? []).map((slot) => slot.scheduledLocal?.date).filter(Boolean)
      return dates.length ? dates : [String(plan.startDate ?? '').slice(0, 10)]
    })
    .filter((date) => /^\d{4}-\d{2}-\d{2}$/.test(date))
    .sort()
    .at(-1)
  if (!lastDay) return nextIterationStart.value
  const [year, month, day] = lastDay.split('-').map(Number)
  const next = new Date(year, month - 1, day + 1)
  const current = nextIterationStart.value
  return current && current > next ? current : next
})

const canConfirmPlan = computed(
  () =>
    canReviewPlan.value &&
    !isGenerating.value &&
    !imagesPending.value &&
    responsibilityAccepted.value &&
    approvableSlots.value.length > 0 &&
    !approvingAll.value
)

// Resolve from the live slot list so the dialog reflects recreate/edit updates.
const selectedSlot = computed(
  () => selectedScheduled.value || slots.value.find((s) => s.id === selectedSlotId.value) || null
)

// The slot can disappear under an open dialog (deleted elsewhere, plan replaced).
// The dialog renders nothing then, so close it instead of leaving an empty scrim.
watch(selectedSlot, (slot, previous) => {
  if (!slot && previous) dialogOpen.value = false
})

async function onReloadPlan() {
  reloadingPlan.value = true
  try {
    await planLoad.run(() => reload(), { operation: 'reload-plan' })
  } catch {
    // The notice stays and shows the next attempt, or the support hint.
  } finally {
    reloadingPlan.value = false
  }
}

async function onReloadScheduled() {
  reloadingScheduled.value = true
  try {
    await scheduledLoad.run(() => refetchScheduled(), { operation: 'reload-scheduled' })
  } catch {
    // The notice stays and shows the next attempt, or the support hint.
  } finally {
    reloadingScheduled.value = false
  }
}

async function refreshPlan() {
  try {
    await refetch()
  } catch (error) {
    reportError('refresh-plan', error, { planId: planId.value })
    showSnackbarMessage('error', t('marketing-agent.refresh-error'))
  }
}

async function refreshScheduled() {
  try {
    await refetchScheduled()
  } catch (error) {
    reportError('refetch-scheduled', error, { planId: planId.value })
    showSnackbarMessage('error', t('marketing-agent.refresh-error'))
  }
}

// Points to the scheduled posts when a post was scheduled but its approval not saved.
function scheduledPostsAction(results) {
  return results.some((r) => r?.code === 'APPROVAL_NOT_SAVED')
    ? [{ text: t('marketing-agent.dialog.view-scheduled'), link: SCHEDULED_POSTS_LINK }]
    : undefined
}

function getSlotErrorMessage(result, fallbackKey) {
  const key = SLOT_ERROR_MESSAGES[result?.code]

  return key ? t(key) : t(fallbackKey)
}

// The most actionable cause among the skipped slots, or '' when none is known.
function skippedReason(results) {
  const codes = new Set(results.map((r) => r?.code))
  const code = SKIP_REASON_PRIORITY.find((c) => codes.has(c))
  return code ? t(SLOT_ERROR_MESSAGES[code]) : ''
}

// SKIPPED results are not exceptions, but they are failures for the user.
function reportSkipped(operation, results) {
  for (const result of results) {
    if (result?.outcome === SlotOutcome.Approved) continue
    reportOutcome(operation, {
      level: 'warning',
      planId: planId.value,
      slotId: result?.slotId ?? null,
      code: result?.code ?? null,
      reason: result?.reason ?? null,
    })
  }
}

async function onCreatePlan(input) {
  responsibilityAccepted.value = false
  let generated

  try {
    generated = await generate(input)
  } catch (error) {
    reportFailure('create-plan', error, 'marketing-agent.create-error')
    return
  }
  const { status, planId: generatedPlanId } = generated

  if (status === GenerateMarketingPlanStatus.NoTopics) {
    showSnackbarMessage('error', t('marketing-agent.empty.topics.text'))
    return
  }
  if (status === GenerateMarketingPlanStatus.NoPlatforms) {
    showSnackbarMessage('error', t('marketing-agent.empty.platforms.text'))
    return
  }
  // Another plan took these days since the wizard opened. Reload the plans so the
  // date picker moves past them; the wizard stays open.
  if (status === GenerateMarketingPlanStatus.PlanOverlap) {
    reportOutcome('create-plan', { level: 'warning', code: status })
    showSnackbarMessage('error', t('marketing-agent.create-overlap'))
    refetchPlans()?.catch(() => {})
    return
  }

  if (status !== GenerateMarketingPlanStatus.Generated) {
    reportOutcome('create-plan', { code: status ?? 'NO_STATUS' })
    showSnackbarMessage('error', t('marketing-agent.create-error'))
    return
  }

  $tracking.event('Marketing Agent', 'Created', 'Plan')
  $tracking.heapEvent('marketing_plan_created', { windowDays: input.windowDays })
  wizardOpen.value = false

  await refreshPlan()
  awaitingSchedulePlanId.value = generatedPlanId
}

// GENERATED only means the backend accepted the request; the schedule worker
// writes the slots later. Confirm success once they arrive, not on the request.
// Waits for the new plan itself: after a failed refresh the previous plan stays
// on screen, and its finished schedule must not count. A failed schedule shows
// its own message in the review panel.
watch([awaitingSchedulePlanId, planId, schedulePending, scheduleError], () => {
  if (!awaitingSchedulePlanId.value || planId.value !== awaitingSchedulePlanId.value) return
  if (schedulePending.value) return
  awaitingSchedulePlanId.value = null
  if (!scheduleError.value) showSnackbarMessage('success', t('marketing-agent.create-success'))
})

const outlinePlatforms = computed(() => [
  ...new Set(slots.value.map((slot) => slot.platform).filter(Boolean)),
])

const outlineSlot = computed(() => {
  if (!outlineEditorOpen.value) return null
  if (selectedSlotId.value) return selectedSlot.value
  return outlinePrefill.value
})

function onSlotClick(slot) {
  if (slot?.source === 'scheduled') {
    selectedSlotId.value = null
    selectedScheduled.value = slot
    dialogOpen.value = true
    return
  }
  selectedScheduled.value = null
  selectedSlotId.value = slot?.id ?? null
  if (slot?.textStatus === MarketingPlanSlotTextStatus.Draft) {
    outlineEditorOpen.value = true
    return
  }
  dialogOpen.value = true
}

function onAddOutlineSlot() {
  selectedSlotId.value = null
  outlinePrefill.value = null
  outlineEditorOpen.value = true
}

function onCreateAt({ date, time }) {
  selectedSlotId.value = null
  outlinePrefill.value = { scheduledLocal: { date, time } }
  outlineEditorOpen.value = true
}

async function deleteSlot(slotId) {
  let results
  try {
    results = await deleteSlots([slotId])
  } catch (error) {
    reportFailure('delete-slot', error, 'marketing-agent.delete-slot-error', {
      planId: planId.value,
      slotId,
    })
    return false
  }

  const result = results[0]

  if (result?.outcome === SlotOutcome.Approved) {
    showSnackbarMessage('success', t('marketing-agent.delete-slot-success'))
  } else {
    reportSkipped('delete-slot', results.length ? results : [{ slotId }])
    showSnackbarMessage('error', getSlotErrorMessage(result, 'marketing-agent.delete-slot-error'))
    return false
  }

  await refreshPlan()
  await refreshScheduled()

  return true
}

// One heap event per slot actually approved, mirroring trackPublishedChannels
// in SocialMediaSharing.vue for the equivalent immediate-publish flow.
// SlotOperationResult carries no platform, so it's resolved from the live slot list.
function trackApprovedSlots(results) {
  results
    .filter((r) => r.outcome === SlotOutcome.Approved)
    .forEach((r) => {
      const platform = slots.value.find((s) => s.id === r.slotId)?.platform
      $tracking.heapEvent('marketing_plan_post_approved', { platform })
    })
}

function notifyApproveResults(results) {
  const approved = results.filter((r) => r.outcome === SlotOutcome.Approved).length
  const skippedResults = results.filter((r) => r.outcome !== SlotOutcome.Approved)
  const skipped = skippedResults.length

  if (approved > 0 && skipped === 0) {
    showSnackbarMessage('success', t('marketing-agent.approve.success', { count: approved }), [
      { text: t('marketing-agent.dialog.view-scheduled'), link: SCHEDULED_POSTS_LINK },
    ])
  } else if (approved > 0) {
    const partial = t('marketing-agent.approve.partial', { approved, skipped })
    const reason = skippedReason(results)
    showSnackbarMessage(
      'warning',
      reason ? `${partial} ${reason}` : partial,
      scheduledPostsAction(results)
    )
  } else {
    const firstError = results.find((r) => r.code)
    const reason = skippedReason(results)

    showSnackbarMessage(
      'error',
      reason || getSlotErrorMessage(firstError, 'marketing-agent.approve.none'),
      scheduledPostsAction(results)
    )
  }
}

// Resolves to true when the slot was scheduled. Errors are reported and shown here.
async function approveSlot(slotId) {
  try {
    const results = await approveSlots([slotId])
    const result = results[0]

    if (result?.outcome === SlotOutcome.Approved) {
      trackApprovedSlots(results)
      showSnackbarMessage('success', t('marketing-agent.approve.success', { count: 1 }), [
        { text: t('marketing-agent.dialog.view-scheduled'), link: SCHEDULED_POSTS_LINK },
      ])
      return true
    }
    reportSkipped('approve-slot', results.length ? results : [{ slotId }])
    showSnackbarMessage(
      'error',
      getSlotErrorMessage(result, 'marketing-agent.approve.none'),
      scheduledPostsAction(results)
    )
    return false
  } catch (error) {
    reportFailure('approve-slot', error, 'marketing-agent.approve-error', {
      planId: planId.value,
      slotId,
    })
    return false
  } finally {
    // Approval can be saved even when the request fails (e.g. the router cuts a
    // slow request), so always load the real state.
    await refreshPlan()
    await refreshScheduled()
  }
}

async function onApproveAll() {
  approvingAll.value = true
  // A confirm that throws must not leave the note from the one before.
  approvalSkips.value = []
  const slotIds = approvableSlots.value.map((s) => s.id)

  try {
    const results = await approveSlots(slotIds)
    reportSkipped('approve-all', results)
    trackApprovedSlots(results)
    notifyApproveResults(results)
    approvalSkips.value = results.filter((r) => r.outcome !== SlotOutcome.Approved)
    responsibilityAccepted.value = false
  } catch (error) {
    reportFailure('approve-all', error, 'marketing-agent.approve-error', {
      planId: planId.value,
      slotCount: slotIds.length,
    })
  } finally {
    // Slots are approved one by one on the server. A request that fails halfway
    // (e.g. the 30s router cut) has still approved some of them.
    await refreshPlan()
    await refreshScheduled()
    approvingAll.value = false
  }
}

function onManageCreate() {
  deleteDialogOpen.value = false
  wizardOpen.value = true
}

async function onDeletePlan(id) {
  deletingPlanId.value = id

  try {
    await deletePlan(id)
  } catch (error) {
    reportFailure('delete-plan', error, 'marketing-agent.delete-plan-error', { planId: id })
    deletingPlanId.value = null
    return
  }

  $tracking.event('Marketing Agent', 'Deleted', 'Plan')
  showSnackbarMessage('success', t('marketing-agent.delete-plan-success'))

  try {
    await refreshPlan()
    await refreshScheduled()
  } finally {
    deletingPlanId.value = null
  }
}

async function onAcceptOutline() {
  try {
    await acceptOutline()
  } catch (error) {
    reportFailure('accept-outline', error, 'marketing-agent.accept-outline-error', {
      planId: planId.value,
    })
    return
  }

  $tracking.heapEvent('marketing_plan_outline_accepted')
  showSnackbarMessage('success', t('marketing-agent.accept-outline-success'))

  await refreshPlan()
}

async function onRemoveOutlineSlot(slotId) {
  const platform = outlineSlot.value?.platform
  let results

  try {
    results = await deleteSlots([slotId])
  } catch (error) {
    reportFailure('delete-outline-slot', error, 'marketing-agent.remove-outline-slot-error', {
      planId: planId.value,
      slotId,
    })
    return
  }

  const result = results[0]

  if (result?.outcome !== SlotOutcome.Approved) {
    reportSkipped('delete-outline-slot', results.length ? results : [{ slotId }])
    showSnackbarMessage(
      'error',
      getSlotErrorMessage(result, 'marketing-agent.remove-outline-slot-error')
    )
    return
  }

  outlineEditorOpen.value = false
  $tracking.heapEvent('marketing_plan_slot_deleted', { platform })
  showSnackbarMessage('success', t('marketing-agent.remove-outline-slot-success'))

  await refreshPlan()
}

async function onSaveOutlineSlot({ slotId, platform, date, time }) {
  savingOutline.value = true

  try {
    if (slotId) {
      try {
        await updateSlotSchedule(slotId, { platform, date, time })
      } catch (error) {
        reportFailure('update-slot-schedule', error, 'marketing-agent.update-outline-slot-error', {
          planId: planId.value,
          slotId,
        })
        return
      }

      $tracking.heapEvent('marketing_plan_slot_rescheduled', { platform })
      showSnackbarMessage('success', t('marketing-agent.update-outline-slot-success'))
    } else {
      try {
        await addSlot({ platform, date, time })
      } catch (error) {
        reportFailure('add-slot', error, 'marketing-agent.add-outline-slot-error', {
          planId: planId.value,
        })
        return
      }

      $tracking.heapEvent('marketing_plan_outline_slot_added', { platform })
      showSnackbarMessage('success', t('marketing-agent.add-outline-slot-success'))
    }

    outlineEditorOpen.value = false

    await refreshPlan()
  } finally {
    savingOutline.value = false
  }
}

async function onUpdateSlotSchedule(slotId, schedule) {
  try {
    await updateSlotSchedule(slotId, schedule)
  } catch (error) {
    reportFailure('update-slot-schedule', error, 'marketing-agent.update-outline-slot-error', {
      planId: planId.value,
      slotId,
    })
    throw error
  }

  showSnackbarMessage('success', t('marketing-agent.update-outline-slot-success'))

  await refreshPlan()
}

// Each slot gets its own retry budget for saving edited text. The dialog
// reopens the editor with the user's text after a failure, so nothing is lost.
const editRetryKey = ref('')
const editRetry = useRetryLimit(() => editRetryKey.value)

async function onUpdateSlotText(slotId, text) {
  editRetryKey.value = `update-slot-text:${slotId}`
  try {
    await editRetry.run(() => updateSlotText(slotId, text), {
      operation: 'update-slot-text',
      context: { planId: planId.value, slotId },
    })
  } catch (error) {
    const state = editRetry.state.value
    const message = getThrownErrorMessage(error, 'marketing-agent.update-text-error')
    showSnackbarMessage(
      'error',
      state.exhausted ? `${message} ${t('marketing-agent.retry.exhausted')}` : message
    )
    throw error
  }
  showSnackbarMessage('success', t('marketing-agent.update-text-success'))
}
</script>

<style scoped>
@media (min-width: 960px) {
  .marketing-agent {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    padding-bottom: 8px;
  }

  .marketing-agent:not(.marketing-agent--embedded) {
    height: calc(100dvh - var(--v-layout-top, 0px) - var(--v-layout-bottom, 0px) - 16px);
    max-height: calc(100dvh - var(--v-layout-top, 0px) - var(--v-layout-bottom, 0px) - 16px);
  }

  .marketing-agent--embedded {
    height: calc(100dvh - var(--v-layout-top, 0px) - 150px);
    max-height: calc(100dvh - var(--v-layout-top, 0px) - 150px);
  }

  .marketing-agent__plan {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
  }

  .plan-shell {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
  }

  .plan-shell__calendar {
    flex: 1;
    min-height: 0;
  }
}

.marketing-agent__wizard {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 360px;
}

.plan-shell {
  position: relative;
  overflow: hidden;
}

.plan-shell__loader {
  position: absolute;
  z-index: 5;
  top: 0;
  left: 0;
}

.plan-shell__notice {
  margin: 8px 8px 0;
}

.plan-approval {
  position: absolute;
  z-index: 4;
  bottom: 16px;
  left: 50%;
  width: min(640px, calc(100% - 24px));
  margin: 0;
  transform: translateX(-50%);
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.16);
}

.plan-approval__title {
  margin: 0;
  padding: 10px 16px;
  border-radius: 6px 6px 0 0;
  background: rgb(var(--v-theme-primary));
  font-size: 16px;
  font-weight: 700;
  line-height: 1.3;
}

.plan-approval__body {
  padding: 16px 18px 14px;
  border: 1px solid #e4e4e4;
  border-top: none;
  border-radius: 0 0 6px 6px;
  background: #fff;
  color: #1a1a1a;
}

.plan-approval__text {
  margin: 0;
  color: #1a1a1a;
  font-size: 15px;
  line-height: 1.5;
}

.plan-approval__text--status {
  display: flex;
  align-items: center;
  gap: 10px;
}

.plan-approval__text--error {
  color: rgb(var(--v-theme-error));
}

.plan-approval__check {
  margin: 8px 0 4px -8px;
}

.plan-approval__check :deep(.v-label) {
  color: #1a1a1a;
  font-size: 14px;
  line-height: 1.45;
  opacity: 1;
  white-space: normal;
}

.plan-approval__check :deep(.v-selection-control) {
  align-items: flex-start;
}

.plan-approval__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
}

.plan-approval__delete {
  margin-right: auto;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.plan-approval__new {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.plan-approval__confirm {
  min-width: 160px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.04em;
}
</style>
